
class SecureSessionService {
  private static readonly API_TIMEOUT = 10000; // 10 seconds

  // Validate both session ID and nonce together
  static async validateSessionAndNonce(
    sessionId: string, 
    nonce: string
  ): Promise<{
    valid: boolean;
    errorType?: 'INVALID_NONCE' | 'SESSION_EXPIRED' | 'GENERIC_ERROR';
    error?: string;
    expiresAt?: number;
  }> {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/okta-profile-update-service/api/v1/auth';
    
      if (!sessionId || sessionId.length < 20) {
        return {
          valid: false,
          errorType: 'GENERIC_ERROR',
          error: 'Invalid session ID format'
        };
      }

      if (!nonce || nonce.length < 20) {
        return {
          valid: false,
          errorType: 'INVALID_NONCE',
          error: 'Invalid nonce format'
        };
      }

      const response = await fetch(`${apiUrl}auth/session/validateNonce`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          sessionId,
          nonce
        }),
        signal: AbortSignal.timeout(this.API_TIMEOUT)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        
        if (response.status === 401) {
          return { 
            valid: false, 
            errorType: data.errorType || 'INVALID_NONCE',
            error: data.error || 'Nonce expired or invalid'
          };
        } else if (response.status === 410) {
          return { 
            valid: false, 
            errorType: 'SESSION_EXPIRED',
            error: data.error || 'Session has expired'
          };
        } else {
          return {
            valid: false,
            errorType: 'GENERIC_ERROR',
            error: data.error || 'Validation failed'
          };
        }
      }

      const data = await response.json();
      
      const isSuccess = data.valid === true || 
                       (response.ok && data.error === "Success") ||
                       (response.ok && data.message?.toLowerCase().includes("validated successfully"));
      
      if (!isSuccess) {
        return { 
          valid: false, 
          errorType: data.errorType || 'GENERIC_ERROR',
          error: data.error !== "Success" ? data.error : data.message || 'Validation failed'
        };
      }

      const expiresHeader = response.headers.get('X-Session-Expires-In-Minutes');
    
      let expiresAt = 0;
      if (expiresHeader && expiresHeader !== '0') {
        const minutes = parseInt(expiresHeader);
        expiresAt = Date.now() + (minutes * 60 * 1000);
      }

      return {
        valid: true,
        expiresAt: expiresAt
      };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { 
          valid: false, 
          errorType: 'GENERIC_ERROR',
          error: 'Request timeout'
        };
      }
      
      return { 
        valid: false, 
        errorType: 'GENERIC_ERROR',
        error: error.message || 'Validation failed'
      };
    }
  }

  static generateClientFingerprint(): string {
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      navigator.hardwareConcurrency || 'unknown',
      navigator.platform
    ].join('|');
    
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return hash.toString(16);
  }

  static isSessionExpired(expiresAt: number): boolean {
    return Date.now() >= expiresAt;
  }

  static storeSession(sessionData: {
    sessionId: string;
    userId: string;
    expiresAt?: number; 
  }): void {
    try {
      const sessionInfo = {
        sessionId: sessionData.sessionId,
        userId: sessionData.userId,
        expiresAt: sessionData.expiresAt || 0,  
        storedAt: Date.now(),
        clientFingerprint: this.generateClientFingerprint()
      };
      
      sessionStorage.setItem('secureSession', JSON.stringify(sessionInfo));
    } catch (error) {
    }
  }

  static getStoredSession(): {
    sessionId: string;
    userId: string;
    expiresAt: number;
  } | null {
    try {
      const stored = sessionStorage.getItem('secureSession');
      if (!stored) {
        return null;
      }

      const sessionInfo = JSON.parse(stored);
      
      const currentFingerprint = this.generateClientFingerprint();
      if (sessionInfo.clientFingerprint !== currentFingerprint) {
        sessionStorage.removeItem('secureSession');
        return null;
      }
      
      if (this.isSessionExpired(sessionInfo.expiresAt)) {
        sessionStorage.removeItem('secureSession');
        return null;
      }

      return sessionInfo;
    } catch (error) {
      sessionStorage.removeItem('secureSession');
      return null;
    }
  }

  /**
   * Reads the raw session from sessionStorage WITHOUT checking expiry.
   * Used ONLY by apiClient to refresh expiresAt when a new header arrives —
   * even if the session just expired, the sessionId is still needed to write
   * a fresh expiresAt before the session is fully discarded.
   * Do NOT use this anywhere else — always use getStoredSession() instead.
   */
  static getRawSession(): {
    sessionId: string;
    userId: string;
    expiresAt: number;
  } | null {
    try {
      const stored = sessionStorage.getItem('secureSession');
      if (!stored) return null;
      const sessionInfo = JSON.parse(stored);
      // Still enforce fingerprint — session must belong to this browser tab
      const currentFingerprint = this.generateClientFingerprint();
      if (sessionInfo.clientFingerprint !== currentFingerprint) {
        sessionStorage.removeItem('secureSession');
        return null;
      }
      return sessionInfo;
    } catch (error) {
      return null;
    }
  }

  static clearSession(): void {
    sessionStorage.removeItem('secureSession');
    sessionStorage.removeItem('samlRelayState');
    sessionStorage.removeItem('username-selection-flow-state');
  }

  static isValidNonceFormat(nonce: string): boolean {
    const nonceRegex = /^[a-zA-Z0-9_-]{32,}$/;
    return nonceRegex.test(nonce);
  }
}

export default SecureSessionService;
